/**
 * One offered continuation.
 *
 * Read at arm's length, in a glance, while your hands are busy. That constrains the
 * information design hard: the chord symbol has to be findable without reading, the
 * relation label tells you *what kind of move* it is, the description is one sentence you
 * can take or leave, and the character word is the bit that actually decides it for most
 * people. Everything past that lives one tap away, inside the card.
 *
 * ## A tap opens. It never takes.
 *
 * "If I click on a note, give me the AI description of why we're going in that direction
 * first, then I click approve."
 *
 * This started as a phone fix — six cards of four lines at 390 points are a wall of small
 * print, and the single tap they offered committed you to a chord you had not finished
 * reading about. It was scoped to phones because an iPad has the room. Then the owner hit
 * the same accident on a bigger screen: a tap on E major took E major, on the spot, and
 * getting back out meant the history detail and a rewind. The accident was never about
 * screen size. It was about a control that reads and a control that commits being the
 * same control.
 *
 * So on **every** viewport this card has two stages. The first tap opens it *in place*:
 * the description, the character word, the notes, and the long explanation fetched
 * through the shared `useExplanation`. Taking it is a second, explicit tap on a
 * full-width button that says "Take this". There is no width at which a tap on a card
 * selects a chord, and there is no sheet — a sheet covers the very column you are
 * choosing from.
 *
 * `expansion` carries the two stages, and it is the presence of that object rather than
 * any width that decides them; `expansion.stacked` is the separate, narrower claim that
 * this is a *phone*, which changes what a shut card shows and how the column makes room.
 * Without `expansion` at all — the stale layer, and nothing else — the card is a picture
 * of a set already left behind and has no controls on it.
 *
 * ## The open card is a screen, not a taller card
 *
 * The first cut let the card grow to fit its contents and let the *column* scroll. On the
 * owner's phone, with a long explanation, that put the card's own chord symbol above the
 * top of the graph area and ran the paragraph under the session header: scrolling moved
 * the whole card, so the thing you were reading about scrolled away from the thing you
 * were reading. So an open card is exactly as tall as the room it is given and is built
 * as three rows —
 *
 *  - a **fixed header**: the chord symbol, the relation, and an × that closes it;
 *  - a **scrolling middle**: description, character, notes, the paragraph, the voicing
 *    hint. This is the only thing that scrolls while a card is open;
 *  - a **fixed footer**: "Hear it" and "Take this", where a thumb left them. Both are
 *    fixed because both are used repeatedly while reading — the second once, the first as
 *    many times as it takes to decide.
 *
 * Two consequences worth naming, and they hold at every width. A tap on the card body
 * does not close it: a long paragraph is read by dragging across the very surface that
 * would otherwise be a close button, and collapsing mid-read loses your place. The × is
 * the way back, and Escape where there is a keyboard. And what happens to the siblings is
 * the one thing that *is* width-dependent — on a phone they are `hidden`, because the
 * open card is the whole column; wider they are `receded`, because there is room to keep
 * the alternatives on screen while you read about one. `BranchColumn` decides which.
 */

import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import type { BranchSuggestion, CanonicalChord, KeyEstimate } from '@hc/shared';
import { ChordSymbol } from '../system/ChordSymbol.js';
import { MiniKeyboard } from '../system/MiniKeyboard.js';
import { pitchClassNames } from '../system/chordReading.js';
import type { ExplanationFetcher } from '../system/sessionView.js';
import { useExplanation } from '../system/useExplanation.js';
import { branchLayoutId, cardTransition } from './motion.js';

/**
 * Everything the two-stage card needs, as one object.
 *
 * A bundle rather than five loose optional props, so that "this card opens before it
 * commits" is a single presence check at every call site and cannot be half-wired — a
 * card that knew it was expanded but not how to fetch its paragraph would open onto an
 * empty box.
 */
export interface BranchCardExpansion {
  expanded: boolean;
  /**
   * Toggles this card. Expanding one collapses whichever was open.
   *
   * While a card is open this is reachable from the × and from Escape, and from nowhere
   * else on the card itself — see the note about body taps at the top of this file.
   */
  onToggle: (suggestion: BranchSuggestion) => void;
  /**
   * This column is on a phone.
   *
   * Not what decides the two stages — those are everywhere now — but what decides how
   * much a *shut* card shows. Stacked, it is a chord symbol and a relation and nothing
   * else, because five four-line cards at 390 points are the wall of small print this
   * whole interaction came out of. Wider, a shut card is exactly the card it always was.
   */
  stacked: boolean;
  /** The same fetcher the explanation sheet is handed. Absent degrades to no paragraph. */
  fetchExplanation: ExplanationFetcher | undefined;
  /** For spelling the notes. The card's `chord` is respelled; its note names must match. */
  keyEstimate: KeyEstimate;
  /**
   * Hear this chord without taking it. Absent at the piano and wherever sound is off.
   *
   * In the bundle rather than beside `onSelect`, and the line between them is the one this
   * card is built on: everything that helps you *read* an option travels together — the
   * paragraph, the spelling, and now the sound — while the single thing that *commits* to
   * one stays a separate prop, so that "a card cannot select" remains a structural fact
   * about where `onSelect` is reachable from.
   */
  onPreview?: ((chord: CanonicalChord) => void) | undefined;
}

export interface BranchCardProps {
  suggestion: BranchSuggestion;
  /** Already respelled for the current key. */
  chord: CanonicalChord;
  index: number;
  calm: boolean;
  /** Faded because it belongs to the previous node and a new set is on its way. */
  stale?: boolean;
  /**
   * This card is filling one slot of a set that is still arriving.
   *
   * It changes the entry only. A whole set appearing at once slides in from the right on a
   * per-card stagger, which reads as the column being dealt; a single card replacing the
   * skeleton that was holding its place has nowhere to slide *from*, so it crossfades where
   * it stands and takes no stagger — the network already staggered it.
   */
  streaming?: boolean;
  /**
   * A sibling of the card that is currently open, on a phone.
   *
   * Hidden rather than unmounted: it keeps its DOM node, its place in the list and
   * anything it has already fetched, so closing the open card puts the set back exactly
   * as it was rather than rebuilding it. See `BranchColumn`.
   */
  hidden?: boolean;
  /**
   * A sibling of the card that is currently open, anywhere there is room to show it.
   *
   * The wide counterpart of `hidden`: still on screen, still tappable — tapping one is
   * how you move from reading about this option to reading about that one — but dimmed
   * and squeezed, so the card being read owns the column without the alternatives having
   * to leave it.
   */
  receded?: boolean;
  /**
   * Present only where a chord can be chosen by hand. Absent is the piano, and the piano
   * is the default — see the note at the top of this file. It draws the footer of an open
   * card and nothing else: no tap anywhere on this component reaches it.
   */
  onSelect?: (suggestion: BranchSuggestion) => void;
  /** The two stages. Absent only on the stale layer. See the top of this file. */
  expansion?: BranchCardExpansion;
}

export function BranchCard({
  suggestion,
  chord,
  index,
  calm,
  stale = false,
  streaming = false,
  hidden = false,
  receded = false,
  onSelect,
  expansion,
}: BranchCardProps): React.JSX.Element {
  const selectable = onSelect !== undefined;
  const settle = calm || streaming;
  /** Open, and therefore showing its paragraph. True at any width. */
  const expanded = expansion?.expanded === true;
  /** Shut, on a phone: a symbol and a label and nothing else. */
  const collapsed = expansion?.stacked === true && !expanded;
  /**
   * The identity block is carrying the chord and the move and nothing more — because it
   * is a shut phone card, or because it is the header of an open one, where the
   * description belongs to the scrolling middle instead.
   */
  const brief = collapsed || expanded;
  const load = useExplanation(suggestion, expansion?.fetchExplanation, expanded);
  const stage = expanded ? ' hc-card--expanded' : collapsed ? ' hc-card--collapsed' : '';
  /** On the Go, with sound: the chord can be auditioned before it is taken. */
  const onPreview = expansion?.onPreview;

  const hitRef = useRef<HTMLButtonElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const wasExpanded = useRef(expanded);

  /*
   * Focus follows the card between its two shapes.
   *
   * Opening removes the button that was pressed and closing removes the × that was
   * pressed, so without this a keyboard or switch user is dropped onto `<body>` twice per
   * card. Coming back, focus is only taken if it was in fact lost: "Take this" also
   * closes the card, and it has moved the graph on to somewhere better to be.
   */
  useEffect(() => {
    if (expanded === wasExpanded.current) return;
    wasExpanded.current = expanded;
    if (expanded) {
      closeRef.current?.focus();
      return;
    }
    const active = document.activeElement;
    if (active === null || active === document.body) hitRef.current?.focus();
  }, [expanded]);

  /*
   * Escape closes an open card, where there is a keyboard to press it on.
   *
   * Not while a sheet is over the top of it: a sheet is the frontmost surface and Escape
   * belongs to whatever is in front. `BottomSheet` listens on `document` too, and two
   * surfaces closing on one keypress is a worse bug than an Escape that did nothing.
   */
  const onToggle = expansion?.onToggle;
  useEffect(() => {
    if (!expanded || !onToggle) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      if (document.querySelector('.hc-sheet-layer')) return;
      onToggle(suggestion);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [expanded, onToggle, suggestion]);

  /*
   * The chord and the move it is, which both shapes of the card show.
   *
   * The grid lives on this span, not on the button that usually wraps it. `<button>` is
   * not a dependable grid container — engines wrap button content in an anonymous box,
   * which lands every child in one cell and stacks the description on top of the label.
   */
  const identity = (
    <span className="hc-card__body">
      <span className="hc-card__symbol">
        <ChordSymbol symbol={chord.symbol} />
      </span>
      {/*
        Dropped in the DOM, not hidden by a media query. Two reasons: what a card shows is
        a claim a test should be able to make, and jsdom has no opinion about `@media`; and
        the stylesheet's own narrow rules hang off `.hc-branches--stacked`, which comes
        from the same boolean as `brief`, so shape and content cannot disagree about which
        device this is.

        In an open card, at any width, both of these belong to the scrolling middle rather
        than to the header: a four-line description in a fixed header is four lines of
        paragraph nobody can scroll.
      */}
      {brief ? null : <span className="hc-card__description">{suggestion.shortDescription}</span>}
      {/*
        Relation and character share one row. Four stacked rows do not fit inside a card
        whose height is the column divided by six on a small iPad, and the two things that
        lose out when they are stacked are the description (starved to nothing by the
        `1fr` track) and the character word (clipped) — which are the two most useful
        things on the card. Both are short, so one line holds them.
      */}
      <span className="hc-card__meta">
        <span className="hc-card__relation">
          {suggestion.relationLabel}
          {suggestion.romanNumeral ? (
            <span className="hc-card__roman"> · {suggestion.romanNumeral}</span>
          ) : null}
        </span>
        {brief ? null : <span className="hc-card__character">{suggestion.character}</span>}
      </span>
    </span>
  );

  return (
    <li
      className={`hc-branch-row${expanded ? ' hc-branch-row--expanded' : ''}${
        receded ? ' hc-branch-row--receded' : ''
      }`}
      // A sibling of the open card on a phone. `hidden` rather than a class, so it leaves
      // the accessibility tree as well as the screen. Wider it is `receded` instead, which
      // is a class on this row and not on the card: the card is a `motion` element and
      // framer-motion writes its opacity inline, where no stylesheet can reach it.
      hidden={hidden}
    >
      <motion.div
        className={`hc-card${stale ? ' hc-card--stale' : ''}${selectable ? ' hc-card--selectable' : ''}${stage}`}
        data-testid="hc-branch-card"
        data-suggestion-id={suggestion.id}
        data-movement-kind={suggestion.movementKind}
        // Mirrored onto the DOM because `layoutId` is invisible to a test, and the claim
        // that this card *becomes* the current node is exactly the claim that the two
        // share one. See `graph.test.tsx`.
        //
        // Dropped while the card is hidden: a `display: none` element measures as a point,
        // and a shared layout identity anchored to a point is how you get the whole set
        // flying out of one corner when the open card is closed again.
        data-layout-id={calm || stale || hidden ? undefined : branchLayoutId(suggestion.id)}
        layoutId={calm || stale || hidden ? undefined : branchLayoutId(suggestion.id)}
        initial={settle ? { opacity: 0 } : { opacity: 0, x: 14 }}
        animate={{ opacity: stale ? 0.28 : 1, x: 0 }}
        exit={calm ? { opacity: 0 } : { opacity: 0, x: 10 }}
        transition={cardTransition(streaming ? 0 : index, calm)}
      >
        {expansion && expansion.expanded ? (
          /*
            The open card's header. A plain box, not a button: the body of an open card is
            the surface a thumb drags across to read the paragraph, and while it was a
            close control every one of those drags was a chance to lose the card.
          */
          <div className="hc-card__head">
            {identity}
            <button
              type="button"
              ref={closeRef}
              className="hc-card__close"
              data-testid="hc-card-close"
              onClick={() => expansion.onToggle(suggestion)}
              aria-label="Back to the options"
              title="Back to the options"
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>
        ) : expansion ? (
          <button
            type="button"
            ref={hitRef}
            className="hc-card__hit"
            data-testid="hc-card-hit"
            // One behaviour, at every width, and this is the whole correction: a tap on a
            // card opens the card. `onSelect` is deliberately not reachable from here —
            // the only thing that takes a chord is the button that says "Take this".
            onClick={() => expansion.onToggle(suggestion)}
            aria-expanded={false}
            aria-label={`${chord.symbol}. ${suggestion.relationLabel}. ${
              brief ? '' : `${suggestion.shortDescription} `
            }Tap to see why.`}
          >
            {identity}
          </button>
        ) : (
          /*
            No expansion is the stale layer, and nothing else: a set the player has already
            left behind. It is a picture rather than a control, so it is not a button —
            those cards are `pointer-events: none` anyway, and a focusable button that does
            nothing is worse than no button at all.
          */
          <div className="hc-card__hit">{identity}</div>
        )}

        {expansion && expansion.expanded ? (
          /*
            The middle row, and the only thing that scrolls while a card is open.
            Everything that can run long lives in here — the description the header gave
            up, the paragraph, the voicing hint, the annotations — so the chord symbol
            above it and the commitment below it stay where the thumb left them.
          */
          <div className="hc-card__detail" data-testid="hc-card-detail">
            <p className="hc-card__description">{suggestion.shortDescription}</p>
            <p className="hc-card__character-line">
              <span className="hc-card__character">{suggestion.character}</span>
            </p>

            <div className="hc-card__notes">
              <MiniKeyboard
                pitchClasses={chord.pitchClasses}
                bassPc={chord.bassPc}
                label={`${chord.symbol} notes`}
              />
              <span className="hc-card__note-names">
                {pitchClassNames(chord, expansion.keyEstimate)}
              </span>
            </div>

            {/*
              Two lines of skeleton, not three: this sits under a card in a column, not in
              a sheet with a screen to itself, and a taller placeholder would push the rest
              of the set off a phone before there was anything to read.
            */}
            {load.status === 'loading' ? (
              <div
                className="hc-card__skeleton-lines"
                data-testid="hc-card-explanation-loading"
                aria-busy="true"
              >
                <span />
                <span />
              </div>
            ) : null}

            {load.status === 'ready' ? (
              <p className="hc-card__long" data-testid="hc-card-explanation">
                {load.text}
              </p>
            ) : null}

            {load.status === 'failed' ? (
              <p className="hc-card__quiet" data-testid="hc-card-explanation-failed">
                The longer note is not available right now.
              </p>
            ) : null}

            {suggestion.voicingHint ? (
              <p className="hc-card__quiet">
                <span className="hc-card__quiet-label">try</span> {suggestion.voicingHint}
              </p>
            ) : null}

            {/*
              The three annotations the explanation *sheet* used to be the only place to
              read. That sheet is gone — the expansion is the explanation now, on every
              viewport — so they come here rather than nowhere. Last in the scroller, under
              the paragraph they qualify: an ambiguity note and an implied key are things
              you look up after the prose has told you what the move is.
            */}
            {suggestion.ambiguityNote ? (
              <p className="hc-card__quiet" data-testid="hc-card-ambiguity">
                {suggestion.ambiguityNote}
              </p>
            ) : null}

            {suggestion.impliedKey ? (
              <p className="hc-card__quiet" data-testid="hc-card-implied-key">
                <span className="hc-card__quiet-label">implies</span> {suggestion.impliedKey.tonic}{' '}
                {suggestion.impliedKey.mode} · {suggestion.impliedKey.certainty}
              </p>
            ) : null}

            {suggestion.styleTags.length > 0 ? (
              <ul className="hc-tags" data-testid="hc-card-tags">
                {suggestion.styleTags.map((tag) => (
                  <li key={tag} className="hc-tag">
                    {tag}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}

        {/*
          The commitment, and now the only thing anywhere in the app that takes a branch by
          hand. Full width and inside the card it belongs to, so there is no aiming to do
          and no doubt about which chord is being accepted. Absent at the piano, where the
          instrument is still the input device and this would quietly become the faster way
          to use the app — an open card there is simply something to read.

          Outside the scrolling middle on purpose: a decision you have to scroll to the end
          of the reasoning to reach is a decision the reasoning is arguing for.

          "Hear it" shares the row, and shares it for the same reason: it has to be
          reachable at every scroll position. "I just want to be able to play the chords I
          see!" is a thing a player does two or three times per card while reading about
          it, and the notes it sounds are the third block down inside the scroller — put
          beside them, it would travel away under the thumb of the person using it. So it
          stays with the other permanent control and is drawn as its opposite: quiet,
          outlined, the width of its own label, and first — a preview reads before the
          thing it previews, and "Take this" keeps the accent fill and the rest of the row.
        */}
        {expanded && (onSelect || onPreview) ? (
          <div className="hc-card__foot">
            {onPreview ? (
              <button
                type="button"
                className="hc-card__hear"
                data-testid="hc-card-hear"
                onClick={() => onPreview(chord)}
                // The symbol is in the header, not in this button's own text, and a screen
                // reader lands on the button alone.
                aria-label={`Hear ${chord.symbol}`}
              >
                Hear it
              </button>
            ) : null}
            {onSelect ? (
              <button
                type="button"
                className="hc-card__take"
                data-testid="hc-card-take"
                onClick={() => onSelect(suggestion)}
              >
                Take this
              </button>
            ) : null}
          </div>
        ) : null}
      </motion.div>
    </li>
  );
}

/**
 * The card-shaped hole a card is about to arrive in.
 *
 * Sized like a real card so the column does not reflow when the set lands — a graph that
 * jumps as suggestions stream in is a graph you cannot read while playing.
 *
 * ## Why the breathing is a CSS keyframe and not a `motion` element
 *
 * It used to be `animate={{ opacity: [0.28, 0.5, 0.28] }}` with `repeat: Infinity`. That
 * was harmless while the skeletons had a layer to themselves; it is a liability now that
 * they share one with the cards filling in around them, because `AnimatePresence` tracks
 * every `motion` element in a subtree it is retiring, and an animation with no end is a
 * poor thing to have it waiting on. A CSS animation is invisible to that bookkeeping — and
 * it is the cheaper thing to run anyway, on the one screen that must never stutter, since
 * five perpetually-animating elements otherwise keep framer-motion's loop awake for as
 * long as the network takes. The reduced-motion floor in `styles/index.css` already stops
 * it for anybody who asked; the `calm` flag stops it here too, so the two agree without
 * depending on each other.
 */
export function BranchCardSkeleton({
  index,
  calm,
  hidden = false,
}: {
  index: number;
  calm: boolean;
  /** A slot still on the wire while another card is open on a phone. See `BranchColumn`. */
  hidden?: boolean;
}): React.JSX.Element {
  return (
    <li className="hc-branch-row" aria-hidden="true" hidden={hidden}>
      <div
        className={`hc-card hc-card--skeleton${calm ? '' : ' hc-card--breathing'}`}
        data-testid="hc-branch-skeleton"
        // Staggered, so five holes read as one column waiting rather than five things
        // flashing in time.
        style={calm ? undefined : { animationDelay: `${(index * 0.12).toFixed(2)}s` }}
      >
        <span className="hc-card__skeleton-line hc-card__skeleton-line--symbol" />
        <span className="hc-card__skeleton-line hc-card__skeleton-line--label" />
        <span className="hc-card__skeleton-line" />
      </div>
    </li>
  );
}
